import {
  DEFAULT_COLLISION_PADDING,
  getNodeSize,
  resolveMoveCommandCollisions,
} from './mapCommands';
import {
  inflateRect,
  routeCrossesRoute,
  routeEdges,
  routeIntersectsRect,
} from './edgeRouting';

function finite(value, fallback = 0) {
  return Number.isFinite(value) ? value : fallback;
}

function relationshipKey(relationship) {
  return `${relationship.parentId}::${relationship.childId}`;
}

function runtimeNodeSize(node, nodeSizes) {
  const runtime = nodeSizes instanceof Map
    ? nodeSizes.get(node.id)
    : nodeSizes?.[node.id];
  return getNodeSize(runtime ? { ...node, layoutSize: runtime } : node);
}

function nodeRect(node, nodeSizes) {
  return {
    x: finite(node?.position?.x),
    y: finite(node?.position?.y),
    ...runtimeNodeSize(node, nodeSizes),
  };
}

function compareConflicts(left, right) {
  return left.key.localeCompare(right.key);
}

/**
 * Validate the complete derived route snapshot. The returned geometry is
 * deterministic and remains display-only; only node positions may be written.
 */
export function inspectSettledGeometry({
  nodes = [],
  relationships = [],
  nodeSizes = null,
  previousRoutes = new Map(),
} = {}) {
  const routeNodes = nodes.map((node) => ({
    id: node.id,
    rect: nodeRect(node, nodeSizes),
  }));
  const rects = new Map(routeNodes.map((node) => [node.id, node.rect]));
  const orderedRelationships = [...relationships]
    .filter((relationship) => rects.has(relationship.parentId)
      && rects.has(relationship.childId))
    .sort((left, right) => (
      String(left.parentId).localeCompare(String(right.parentId))
      || String(left.childId).localeCompare(String(right.childId))
    ));
  const routes = routeEdges({
    nodes: routeNodes,
    edges: orderedRelationships,
    previousRoutes,
  });
  const conflicts = [];

  orderedRelationships.forEach((relationship) => {
    const edgeKey = relationshipKey(relationship);
    const route = routes.get(edgeKey);
    if (!route) return;
    routeNodes.forEach((node) => {
      if (node.id === relationship.parentId || node.id === relationship.childId) return;
      if (!routeIntersectsRect(route, inflateRect(node.rect))) return;
      conflicts.push({
        type: 'node',
        key: `node:${edgeKey}:${node.id}`,
        edgeKeys: [edgeKey],
        nodeIds: [relationship.parentId, relationship.childId, node.id],
        obstacleNodeId: node.id,
      });
    });
  });

  for (let leftIndex = 0; leftIndex < orderedRelationships.length; leftIndex += 1) {
    const leftRelationship = orderedRelationships[leftIndex];
    const leftKey = relationshipKey(leftRelationship);
    const leftRoute = routes.get(leftKey);
    if (!leftRoute) continue;
    for (
      let rightIndex = leftIndex + 1;
      rightIndex < orderedRelationships.length;
      rightIndex += 1
    ) {
      const rightRelationship = orderedRelationships[rightIndex];
      const rightKey = relationshipKey(rightRelationship);
      const rightRoute = routes.get(rightKey);
      if (!rightRoute || !routeCrossesRoute(leftRoute, rightRoute)) continue;
      conflicts.push({
        type: 'edge',
        key: `edge:${leftKey}:${rightKey}`,
        edgeKeys: [leftKey, rightKey],
        nodeIds: [
          leftRelationship.parentId,
          leftRelationship.childId,
          rightRelationship.parentId,
          rightRelationship.childId,
        ],
      });
    }
  }

  conflicts.sort(compareConflicts);
  return {
    valid: conflicts.length === 0,
    conflictCount: conflicts.length,
    conflicts,
    routes,
  };
}

/**
 * Resolve one completed drag against the canonical graph snapshot. Candidate
 * ordering is stable, subtree offsets are preserved, and every returned
 * position can be committed in one existing move/reparent transaction.
 */
/**
 * Resolve one completed drag against the canonical graph snapshot.
 *
 * This used to continue into a search that nudged nearby stationary branches
 * until a whole-map route snapshot came back crossing-free. Each candidate it
 * tried re-routed the visible graph, which put a re-route inside three nested
 * loops: a drag on a parent with sixteen children took about four seconds on
 * the main thread, and past roughly twenty-four the search gave up and left
 * the map as it found it.
 *
 * The search existed to untangle connections that free positioning had
 * tangled. Automatic placement is now structured, so the arrangement it was
 * repairing is not created in the first place, and a branch that does need
 * straightening has an explicit Tidy command that runs in linear time.
 *
 * What remains is the part that was always cheap and always needed: the moved
 * subtree keeps its shape and is separated from stationary ideas with a
 * visible gap.
 */
export function settleReleasedSubtree(command, {
  nodes = [],
  nodeSizes = null,
  padding = DEFAULT_COLLISION_PADDING,
} = {}) {
  const collisionCommand = resolveMoveCommandCollisions(command, {
    nodes,
    nodeSizes,
    padding,
  });

  return {
    ...collisionCommand,
    settlingValidated: true,
    settlingResolved: false,
    settledBranchRootIds: [],
    settlingConflictCount: 0,
    settlingExhausted: false,
  };
}
